import { TDevice } from '@cian/utils';
import { applyMiddleware, combineReducers, compose, createStore as _createStore, Store, StoreEnhancer } from 'redux';
import thunk from 'redux-thunk';

import { showFeedbackReducer } from './feedback';
import { galleryReducer, IGalleryData } from './gallery';
import { createSimpleReducer, booleanReducer } from './helpers';
import { IMapState, mapReducer } from './map';
import { IMobile, mobileReducer } from './mobile';
import { INavigationLinksState, navigationLinksReducer } from './navigation';
import { DEFAULT_SUBDOMAIN } from '../constants';
import {
  TMagazineModel,
  IGenplanScheme,
  TExperimentResult,
  TUser,
  IUserGaDataLayerResponseSchema,
  TGetVillagePromotionsResponse,
  IGetVillageResponseSchema,
} from '../types';
import { IReduxThunkContext } from '../types/store';
import { TReduxActions } from '../types/store/actions';
import { isClient } from '../utils/helpers';

interface IDeviceState {
  deviceType: TDevice;
}

export interface IState {
  abGroup: number;
  subdomain: string;
  countrysideRoadRegions: number[];
  countrysideStationRegions: number[];
  villageData: IGetVillageResponseSchema;
  promotionsVillageData: TGetVillagePromotionsResponse;
  gallery: IGalleryData;
  showFeedback: boolean;
  magazine: TMagazineModel[];
  map: IMapState;
  mobile: IMobile;
  device: IDeviceState;
  navigationLinks: INavigationLinksState;
  userData?: TUser;
  userGaData?: IUserGaDataLayerResponseSchema | null;
  countrysideObjectsGalleryEnabled: boolean;
  isFatCard: boolean;
  isCroppedCard: boolean;
  buildAndSaledBlockShow: boolean;
  abUseExperiments: TExperimentResult[];
  genplan: IGenplanScheme | null;
}

export function createStore(context: IReduxThunkContext, preloadedState: IState) {
  const reducer = combineReducers<IState, TReduxActions>({
    abGroup: createSimpleReducer(100),
    subdomain: createSimpleReducer(DEFAULT_SUBDOMAIN),
    countrysideRoadRegions: createSimpleReducer<number[]>([]),
    countrysideStationRegions: createSimpleReducer<number[]>([]),
    countrysideObjectsGalleryEnabled: booleanReducer,
    isFatCard: booleanReducer,
    isCroppedCard: booleanReducer,
    gallery: galleryReducer,
    magazine: createSimpleReducer<TMagazineModel[]>([]),
    map: mapReducer,
    mobile: mobileReducer,
    device: createSimpleReducer<IDeviceState>({ deviceType: 'desktop' }),
    navigationLinks: navigationLinksReducer,
    showFeedback: showFeedbackReducer,
    userData: createSimpleReducer<TUser>({ isAuthenticated: false } as TUser),
    userGaData: createSimpleReducer<IUserGaDataLayerResponseSchema | null>(null),
    villageData: createSimpleReducer<IGetVillageResponseSchema>({} as IGetVillageResponseSchema),
    promotionsVillageData: createSimpleReducer<TGetVillagePromotionsResponse>({} as TGetVillagePromotionsResponse),
    buildAndSaledBlockShow: booleanReducer,
    abUseExperiments: createSimpleReducer<TExperimentResult[]>([{} as TExperimentResult]),
    genplan: createSimpleReducer<IGenplanScheme | null>(null),
  });

  const composeEnhancers = (isClient() && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

  return _createStore<IState, TReduxActions, IState, {}>(
    reducer,
    preloadedState as IState,
    composeEnhancers(applyMiddleware(thunk.withExtraArgument(context))) as unknown as StoreEnhancer<IState>,
  );
}

export type TReduxStore = Store<IState, TReduxActions>;
